import { useTranslate } from '@tolgee/react';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { CheckSvg } from '../../../assets/icons';
import Editsvg from '../../../assets/icons/EditSvg';
import { MagicWandSvg } from '../../../assets/icons/MagicWandSvg';
import theme from '../../../assets/theme';
import { AiAssessedScore } from '../../../frontendServices/gameAPI';
import useAiAssessedScore from '../../../hooks/useAiAssessedScore';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useEditAiAssessedScore from '../../../hooks/useEditAiAssessedScore';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { Response } from '../../../services/backendService/types';
import { MarkdownSlateViewer } from '../../_organisms/DesignerTabPane/AgentTab/MarkdownSlateViewer/MarkdownSlateViewer';
import { TextArea } from '../_TextArea';
import { Button } from '../Button';
import { LoadingDots } from '../DismissableQuestionList/DismissableQuestionList';

const AiAssessedAnswerListItem = ({
  response,
  aiAssessedScore,
  isFetchingAiAssessedScores,
  gameId,
  slideId,
  participantName,
}: {
  response: Response;
  aiAssessedScore?: AiAssessedScore;
  isFetchingAiAssessedScores: boolean;
  gameId: string;
  slideId: string;
  participantName: string;
}) => {
  const { t } = useTranslate();
  const { tenantId } = useRouterMatch();
  const [isEditing, setIsEditing] = useState(false);
  const metrics = useMetrics();
  const isDismissed = response.dismissed;
  const isLargerThan650 = useBreakPoint(650);
  const {
    updateAiAssessedSingleScore,
    aiAssessedScoreSingle,
    isFetchingSingle,
  } = useAiAssessedScore(gameId, slideId, tenantId, [response.id]);
  const [personalisedFeedback, setPersonalisedFeedback] = useState(
    aiAssessedScore?.feedback,
  );
  const { saveEditedAiAssessedSingleScore, isSavingSingle } = useEditAiAssessedScore(
    gameId,
    slideId,
    tenantId,
    response.id,
    personalisedFeedback,
  );

  const onRetry = () => {
    updateAiAssessedSingleScore();
    metrics.logEvent('AiAssessedAnswers.RetryAssessment.Clicked', {
      gameId,
      roundId: slideId,
      reponseId: response.id,
    });
  };

  useEffect(() => {
    if (aiAssessedScoreSingle) setPersonalisedFeedback(aiAssessedScoreSingle.feedback);
  }, [aiAssessedScoreSingle]);

  const saveEdits = useCallback(() => {
    saveEditedAiAssessedSingleScore();
    metrics.logEvent('AiAssessedAnswers.EditAssessment.Clicked', {
      gameId,
      roundId: slideId,
      reponseId: response.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveEditedAiAssessedSingleScore]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAutoSaveEdits = useCallback(
    debounce(async (edited: string, existing: string) => {
      if (JSON.stringify(edited) !== JSON.stringify(existing)) {
        saveEdits();
      }
    }, 1000),
    [],
  );

  useEffect(() => {
    if (personalisedFeedback && aiAssessedScore) {
      debouncedAutoSaveEdits(personalisedFeedback, aiAssessedScore.feedback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalisedFeedback, isEditing]);

  return (
    <FeedbackResponseItemContainer $dismissed={isDismissed}>
      <VerticalContainer>
        {!isDismissed && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: 10,
            }}
          >
            <h2>
              Feedback{' '}
              {!isLargerThan650 && (
                <span
                  style={{
                    fontSize: '16px',
                    color: theme.colors.iconGrey,
                  }}
                >
                  ({participantName})
                </span>
              )}
            </h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 5 }}>
              <Button
                onClick={() => onRetry()}
                icon={<MagicWandSvg />}
                height="32px"
                styles={{
                  color: theme.colors.green,
                  borderColor: theme.colors.green,
                  padding: '0px 5px',
                }}
                type="tertiary"
              >
                {t('Retry')}
              </Button>
              <Button
                onClick={() => {
                  setIsEditing(!isEditing);
                }}
                loading={isSavingSingle}
                icon={isEditing ? <CheckSvg /> : <Editsvg />}
                height="32px"
                styles={{
                  color: theme.colors.blue,
                  borderColor: theme.colors.blue,
                  padding: '0px 5px',
                }}
                type="tertiary"
              >
                {isEditing ? 'Done' : t('Edit')}
              </Button>
            </div>
          </div>
        )}
        <span style={{ height: '100%', width: '100%' }}>
          {(isFetchingAiAssessedScores || isFetchingSingle) && (
            <p>
              Loading<LoadingDots>...</LoadingDots>
            </p>
          )}
          {/* show retried feedback */}
          {!isFetchingAiAssessedScores &&
            !isFetchingSingle &&
            aiAssessedScoreSingle &&
            !isEditing && (
              <MarkdownSlateViewer
                content={aiAssessedScoreSingle.feedback}
                fontSize={16}
              />
            )}
          {/* show edit feedback textarea */}
          {!isFetchingAiAssessedScores &&
            !isFetchingSingle &&
            aiAssessedScore &&
            isEditing && (
              <>
                <TextArea
                  style={{
                    height: '100%',
                    minWidth: '100%',
                    fontSize: 16,
                    paddingBottom: 15,
                  }}
                  textAreaStyled={{
                    height: '100%',
                    minWidth: '100%',
                    fontWeight: 'bold',
                    lineHeight: 1.5,
                  }}
                  value={personalisedFeedback}
                  onChange={e => setPersonalisedFeedback(e.target.value)}
                />
              </>
            )}
          {/* show feedback */}
          {!isFetchingAiAssessedScores &&
            !isFetchingSingle &&
            aiAssessedScore &&
            !aiAssessedScoreSingle &&
            !isEditing && (
              <MarkdownSlateViewer content={personalisedFeedback || ''} fontSize={16} />
            )}
        </span>
      </VerticalContainer>
    </FeedbackResponseItemContainer>
  );
};

const VerticalContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: end;
  gap: 5px;
  height: 100%;
  h2 {
    font-size: 36px;
    margin: 0;
  }
  @media (max-width: 1000px) {
    h2 {
      font-size: 24px;
    }
  }
`;

const FeedbackResponseItemContainer = styled.div<{
  $dismissed?: boolean;
}>`
  width: 100%;
  padding: 20px;
  padding-bottom: 5px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: ${theme.colors.white};
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  margin-bottom: 10px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  justify-content: start;
  ${({ $dismissed }) =>
    $dismissed &&
    css`
      background-color: rgba(255, 255, 255, 0.5);
      box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
      transform: scale(0.95);
    `}
`;

export default AiAssessedAnswerListItem;
